import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { ChevronDown, ChevronUp, Loader2, Pencil, AlertCircle } from 'lucide-react';

import { ROUTES } from 'features/routes';
import { CampaignInfoProps } from '../../types';
import { getLabels } from './utils';
import { getData } from 'services/api';

import {
	CampaignContainer,
	InfoFlex,
	Label,
	LabeledGroup,
	InfoText,
	StatsContainer,
	StatsGrid,
	StatsItem,
} from './CampaignInfo.styles';
import { airbrake } from 'ErrorBoundary';

interface CampaignStats {
	ab_phase: string;
	ab_variation: string;
	aggregation: string;
	stats: {
		bounce_drops: number;
		bounces: number;
		clicks: number;
		unique_clicks: number;
		delivered: number;
		invalid_emails: number;
		opens: number;
		unique_opens: number;
		requests: number;
		spam_report_drops: number;
		spam_reports: number;
		unsubscribes: number;
	};
	id: string;
}

export const CampaignInfo = ({
	campaignData: { id, name, emailsSent, focus, status },
	isPunchup = false,
}: CampaignInfoProps & { isPunchup?: boolean }) => {
	const { push } = useHistory();
	const { t } = useTranslation();

	const [isExpanded, setIsExpanded] = useState(false);
	const [stats, setStats] = useState<CampaignStats[]>([]);
	const [isLoadingStats, setIsLoadingStats] = useState(false);

	const isDraft = status === 'DRAFT';
	const isSent = status === 'SENT';
	const isFailed = status === 'PROCESSING_FAILED' || status === 'LIST_CREATION_FAILED' || status === 'CONTACTS_FAILED';

	const handleClick = async () => {
		if (isDraft || isFailed) {
			push(ROUTES.EDIT_CAMPAIGN_PATH.replace(':campaignId', `${id}`));
		} else if (isSent && !isExpanded) {
			setIsLoadingStats(true);
			try {
				const statsData = await getData(`/marketing/campaigns/${id}/stats`);
				setStats(statsData);
				setIsExpanded(true);
			} catch (error) {
				airbrake.notify(error);
			} finally {
				setIsLoadingStats(false);
			}
		} else if (isSent && isExpanded) {
			setIsExpanded(false);
		}
	};

	const formatStatValue = (value: number) => {
		return value.toLocaleString();
	};

	const getOpenRate = (opens: number, delivered: number) => {
		if (delivered === 0) return '0%';
		return `${((opens / delivered) * 100).toFixed(1)}%`;
	};

	const getClickRate = (clicks: number, delivered: number) => {
		if (delivered === 0) return '0%';
		return `${((clicks / delivered) * 100).toFixed(1)}%`;
	};

	return (
		<>
			<CampaignContainer
				isDraft={isDraft}
				isSent={isSent}
				isFailed={isFailed}
				onClick={handleClick}
				isPunchup={isPunchup}
			>
				<InfoFlex>
					<InfoText extraWidth isPunchup={isPunchup}>
						{name}
					</InfoText>
					{getLabels(emailsSent, focus, status, t).map(({ extraWidth, label, data }) => (
						<LabeledGroup key={label} extraWidth={extraWidth}>
							<Label isPunchup={isPunchup}>{t(`marketing-campaignInfo-${label}`)}</Label>
							<InfoText isPunchup={isPunchup}>{data}</InfoText>
						</LabeledGroup>
					))}
				</InfoFlex>
				{isDraft && <Pencil size={20} />}
				{isFailed && <AlertCircle size={20} color="#ff4444" />}
				{isSent &&
					(isLoadingStats ? (
						<Loader2 size={20} className="animate-spin" />
					) : isExpanded ? (
						<ChevronUp size={20} />
					) : (
						<ChevronDown size={20} />
					))}
			</CampaignContainer>

			{isExpanded && stats.length > 0 && (
				<StatsContainer isPunchup={isPunchup}>
					<StatsGrid>
						<StatsItem>
							<Label isPunchup={isPunchup}>Delivered</Label>
							<InfoText isPunchup={isPunchup}>{formatStatValue(stats[0].stats.delivered)}</InfoText>
						</StatsItem>
						<StatsItem>
							<Label isPunchup={isPunchup}>Opens</Label>
							<InfoText isPunchup={isPunchup}>
								{formatStatValue(stats[0].stats.opens)} ({getOpenRate(stats[0].stats.opens, stats[0].stats.delivered)})
							</InfoText>
						</StatsItem>
						<StatsItem>
							<Label isPunchup={isPunchup}>Unique Opens</Label>
							<InfoText isPunchup={isPunchup}>{formatStatValue(stats[0].stats.unique_opens)}</InfoText>
						</StatsItem>
						<StatsItem>
							<Label isPunchup={isPunchup}>Clicks</Label>
							<InfoText isPunchup={isPunchup}>
								{formatStatValue(stats[0].stats.clicks)} (
								{getClickRate(stats[0].stats.clicks, stats[0].stats.delivered)})
							</InfoText>
						</StatsItem>
						<StatsItem>
							<Label isPunchup={isPunchup}>Unique Clicks</Label>
							<InfoText isPunchup={isPunchup}>{formatStatValue(stats[0].stats.unique_clicks)}</InfoText>
						</StatsItem>
						<StatsItem>
							<Label isPunchup={isPunchup}>Bounces</Label>
							<InfoText isPunchup={isPunchup}>{formatStatValue(stats[0].stats.bounces)}</InfoText>
						</StatsItem>
						<StatsItem>
							<Label isPunchup={isPunchup}>Unsubscribes</Label>
							<InfoText isPunchup={isPunchup}>{formatStatValue(stats[0].stats.unsubscribes)}</InfoText>
						</StatsItem>
						<StatsItem>
							<Label isPunchup={isPunchup}>Spam Reports</Label>
							<InfoText isPunchup={isPunchup}>{formatStatValue(stats[0].stats.spam_reports)}</InfoText>
						</StatsItem>
					</StatsGrid>
				</StatsContainer>
			)}
		</>
	);
};
